/**
 * GiCard — one station in the Vault's mission control.
 *
 * The card is a *workspace* element: it surfaces the GI's identity,
 * current status, and every action the user can perform on it.
 * Coming-soon actions (Publish to Marketplace, Add to Conductor,
 * Connect, Fork) render disabled with a "soon" tag so the Vault
 * doesn't need restructuring when those features land.
 *
 * Accessibility: the root is a link to the GI detail page; inner
 * action buttons stop propagation so "Open" is still the card's
 * primary focus target.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import type { MouseEvent } from 'react';

import { DomainBadge, StatusBadge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { DomainIcon } from '@/components/DomainIcon';
import { ProofStatusBadge } from '@/components/ProofStatusBadge';
import type { ProofStatus } from '@/components/ProofStatusBadge';
import { PublishDialog } from '@/features/marketplace/PublishDialog';
import { GiCardConnectorChips } from '@/features/connectors/GiCardConnectorChips';

import type { GiSummary } from '@/api/types';
import { useLocaleStore } from '@/stores/localeStore';
import {
  normalizeDomain,
  relativeFromTimestamp,
} from '@/utils/format';

export function GiCard({
  gi,
  proofStatus,
}: {
  gi: GiSummary;
  proofStatus?: ProofStatus;
}) {
  const { t } = useTranslation(['vault', 'common']);
  const locale = useLocaleStore((s) => s.locale);
  const navigate = useNavigate();
  const [publishOpen, setPublishOpen] = useState(false);

  const isEmbedded = useMemo(() => {
    try { return localStorage.getItem(`gi.embedded.${gi.root_id}`) === '1'; }
    catch { return false; }
  }, [gi.root_id]);

  const normalized = normalizeDomain(gi.domain);
  const domainLabel =
    typeof gi.domain === 'string'
      ? t(`domain.${gi.domain}`, { ns: 'common' })
      : gi.domain.Custom;

  const stopPropagation = (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const openPublish = (e: MouseEvent) => {
    stopPropagation(e);
    setPublishOpen(true);
  };

  const openConductor = (e: MouseEvent) => {
    stopPropagation(e);
    navigate(`/blueprint?add=${gi.root_id}`);
  };

  const openConnect = (e: MouseEvent) => {
    stopPropagation(e);
    navigate(`/connectors?gi=${gi.root_id}`);
  };

  const isPublished = gi.status.includes('published');

  return (
    <Link
      to={`/gi/${gi.root_id}`}
      aria-label={`${gi.name} — ${domainLabel}`}
      className="group relative flex flex-col rounded-lg border border-border bg-surface p-5 shadow-subtle transition-all hover:-translate-y-0.5 hover:border-border-strong hover:shadow-raised focus-visible:-translate-y-0.5"
    >
      {/* Header row — domain icon + version + proof status */}
      <div className="flex items-center justify-between gap-2">
        <span
          className={`flex h-9 w-9 items-center justify-center rounded-md bg-domain-${normalized}-soft text-domain-${normalized}`}
        >
          <DomainIcon domain={normalized} className="h-5 w-5" />
        </span>
        <div className="flex items-center gap-2">
          {isEmbedded ? (
            <ProofStatusBadge status={proofStatus ?? 'VALID'} size="sm" />
          ) : proofStatus !== undefined ? (
            <ProofStatusBadge status={proofStatus} size="sm" />
          ) : (
            <span className="inline-flex items-center rounded-full border border-border bg-canvas px-2 py-0.5 text-[10px] font-medium text-ink-subtle">
              Standard
            </span>
          )}
          <span className="font-mono text-[11px] text-ink-subtle">v{gi.version}</span>
        </div>
      </div>

      {/* Name + domain badge */}
      <div className="mt-4">
        <DomainBadge domain={normalized} label={domainLabel} />
        <h3 className="mt-2 text-base font-semibold leading-tight text-ink">
          {gi.name}
        </h3>
        <p className="mt-1 line-clamp-2 text-sm text-ink-muted">{gi.description}</p>
      </div>

      {/* Status chips */}
      {gi.status.length > 0 ? (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {gi.status.map((s) => (
            <StatusBadge
              key={s}
              status={s}
              label={t(`status.${s}`, { ns: 'common' })}
            />
          ))}
        </div>
      ) : null}

      {/* Linked connectors (hidden when none) */}
      <GiCardConnectorChips root_id={gi.root_id} />

      {/* Micro-stats row */}
      <div className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-ink-subtle">
        <StatLine
          label={t('card.stats.executions', { count: gi.stats.executions })}
        />
        <StatLine
          label={t('card.stats.verifications', { count: gi.stats.verifications })}
        />
        {gi.stats.forks > 0 ? (
          <StatLine label={t('card.stats.forks', { count: gi.stats.forks })} />
        ) : null}
      </div>

      {/* Action dock */}
      <div
        onClick={stopPropagation}
        className="mt-4 grid grid-cols-2 gap-2 border-t border-border pt-3"
        role="group"
        aria-label={`${gi.name} actions`}
      >
        <Button variant="secondary" size="sm">
          {t('card.actions.run')}
        </Button>
        <Button variant="secondary" size="sm">
          {t('card.actions.verify')}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={openPublish}
          disabled={isPublished}
        >
          {isPublished ? t('status.published', { ns: 'common' }) : t('card.actions.publish')}
        </Button>
        <Button variant="ghost" size="sm" onClick={openConductor}>
          {t('card.actions.add_to_conductor')}
        </Button>
        <Button variant="ghost" size="sm" onClick={openConnect}>
          {t('card.actions.connect')}
        </Button>
        <Button variant="ghost" size="sm" comingSoon>
          {t('card.actions.fork')}
        </Button>
      </div>

      {/* Timestamp pinned bottom */}
      <div className="mt-3 flex items-center justify-between text-[11px] text-ink-subtle">
        <span>{gi.author_name}</span>
        <span>
          <time dateTime={gi.created_at.iso}>
            {relativeFromTimestamp(gi.created_at, locale)}
          </time>
        </span>
      </div>

      <PublishDialog gi={gi} open={publishOpen} onOpenChange={setPublishOpen} />
    </Link>
  );
}

function StatLine({ label }: { label: string }) {
  return <span className="tabular-nums">{label}</span>;
}
